import React, { useCallback, useMemo, useState } from 'react';
import Popover from '@material-ui/core/Popover';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import { HexColorPicker } from 'react-colorful';
import { isValidHexColor, normalizeHexColor } from '../utils';

const PICKER_FALLBACK = '#667085';

const useStyles = makeStyles(() => ({
  trigger: {
    width: '100%',
    height: 44,
    padding: '0 12px',
    marginTop: 12,
    marginBottom: 12,
    border: '1px solid rgba(228, 231, 236, 1)',
    borderRadius: 8,
    background: 'rgba(254, 254, 254, 1)',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    fontFamily: 'Lato',
    fontWeight: 500,
    fontSize: 12,
    color: '#667085',
    textAlign: 'left',
    outline: 'none',
    '&:hover': {
      borderColor: '#d0d5dd',
    },
  },
  triggerValueRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    color: '#101828',
  },
  triggerSwatch: {
    width: 20,
    height: 20,
    borderRadius: 4,
    flexShrink: 0,
    border: '1px solid rgba(228, 231, 236, 1)',
  },
  triggerHex: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: 'monospace',
    letterSpacing: '0.02em',
  },
  placeholder: {
    color: '#98a2b3',
  },
  paper: {
    padding: 16,
    width: 280,
    maxWidth: 'calc(100vw - 32px)',
  },
  picker: {
    width: '100%',
    '& .react-colorful': {
      width: '100%',
      height: 200,
    },
    '& .react-colorful__saturation': {
      borderRadius: '6px 6px 0 0',
    },
    '& .react-colorful__hue': {
      height: 14,
      borderRadius: '0 0 6px 6px',
    },
  },
  hexRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 12,
  },
  preview: {
    width: 36,
    height: 36,
    borderRadius: 6,
    flexShrink: 0,
    border: '1px solid rgba(228, 231, 236, 1)',
  },
  hexField: {
    flex: 1,
    '& .MuiInputBase-input': {
      fontFamily: 'monospace',
      fontSize: 13,
    },
  },
}));

const RnpPrimaryColorSelect = ({ value, onChange }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const normalized = useMemo(() => normalizeHexColor(value), [value]);
  const pickerColor = isValidHexColor(value) ? normalized : PICKER_FALLBACK;

  const handleOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handlePickerChange = useCallback(
    (hex) => {
      onChange(hex);
    },
    [onChange],
  );

  const handleHexInput = useCallback(
    (e) => {
      let v = e.target.value;
      if (v && !v.startsWith('#')) {
        v = `#${v}`;
      }
      if (v.length > 7) {
        v = v.slice(0, 7);
      }
      onChange(v);
    },
    [onChange],
  );

  const handleHexBlur = useCallback(() => {
    const n = normalizeHexColor(value);
    if (n) {
      onChange(n);
    }
  }, [onChange, value]);

  return (
    <>
      <button type="button" className={classes.trigger} onClick={handleOpen}>
        {isValidHexColor(value) ? (
          <span className={classes.triggerValueRow}>
            <span className={classes.triggerSwatch} style={{ backgroundColor: normalized }} />
            <span className={classes.triggerHex}>{normalized}</span>
          </span>
        ) : (
          <span className={classes.placeholder}>Select RNP Background Color</span>
        )}
        <ArrowDropDown style={{ color: '#667085', flexShrink: 0 }} />
      </button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          className: classes.paper,
          style: { zIndex: 10000 },
        }}
        disableRestoreFocus
      >
        <div className={classes.picker}>
          <HexColorPicker color={pickerColor} onChange={handlePickerChange} />
        </div>
        <div className={classes.hexRow}>
          <div className={classes.preview} style={{ backgroundColor: pickerColor }} />
          <TextField
            className={classes.hexField}
            variant="outlined"
            size="small"
            placeholder="#000000"
            value={value || ''}
            onChange={handleHexInput}
            onBlur={handleHexBlur}
            fullWidth
            inputProps={{ maxLength: 7, 'aria-label': 'Hex color' }}
          />
        </div>
      </Popover>
    </>
  );
};

export default RnpPrimaryColorSelect;
